import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { notificationService, type MemoryNotification } from '@/services/notificationService';
import type { PhotoDetail } from '@/types/photo';

const categoryLabel: Record<string, string> = {
  last_year: '작년 오늘',
  past_today: '과거의 오늘',
  last_month: '지난달 오늘',
  photo_day: '사진이 있는 날',
};

export default function MemoryNotificationPage() {
  const navigate = useNavigate();
  const { notificationId = '' } = useParams();
  const [notification, setNotification] = useState<MemoryNotification | null>(null);
  const [photos, setPhotos] = useState<PhotoDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const id = Number(notificationId);
    if (!Number.isInteger(id) || id <= 0) {
      setError('잘못된 알림 주소입니다.');
      setLoading(false);
      return;
    }

    notificationService.getMemoryPhotos(id)
      .then((result) => {
        setNotification(result.notification);
        setPhotos(result.photos);
      })
      .catch(() => setError('추억 사진을 불러오지 못했습니다. 알림이 7일이 지나 자동 정리되었을 수 있습니다.'))
      .finally(() => setLoading(false));
  }, [notificationId]);

  return (
    <div className="min-h-screen bg-background-50 pb-28">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-background-200 bg-background-50/95 px-4 py-4 backdrop-blur">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary-100"
          aria-label="뒤로 가기"
        >
          <i className="ri-arrow-left-line text-xl" />
        </button>
        <div className="min-w-0">
          <h1 className="truncate text-xl font-bold text-foreground-950">
            {notification ? categoryLabel[notification.category] : '추억 사진'}
          </h1>
          {notification && (
            <p className="truncate text-xs text-foreground-400">
              {notification.memoryDate} · 사진 {notification.photoCount}장
            </p>
          )}
        </div>
      </header>

      <main className="p-4">
        {loading ? (
          <div className="py-16 text-center text-foreground-400">사진을 불러오는 중...</div>
        ) : error ? (
          <div className="rounded-3xl border border-background-200 bg-white p-10 text-center">
            <i className="ri-image-off-line text-3xl text-foreground-300" />
            <p className="mt-4 text-sm leading-relaxed text-foreground-500">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/notifications')}
              className="mt-5 rounded-2xl bg-foreground-900 px-5 py-3 text-sm font-semibold text-white"
            >
              받은 알림으로 돌아가기
            </button>
          </div>
        ) : (
          <>
            {notification && (
              <section className="mb-4 rounded-3xl bg-foreground-900 p-5 text-white">
                <span className="text-xs font-semibold text-accent-300">
                  {categoryLabel[notification.category]}
                </span>
                <h2 className="mt-2 text-xl font-bold">{notification.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{notification.body}</p>
              </section>
            )}

            <section className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {photos.map((photo) => (
                <button
                  key={photo.id}
                  type="button"
                  onClick={() => navigate(`/albums/${photo.albumId}/photos/${photo.id}`)}
                  className="group relative aspect-square overflow-hidden rounded-2xl bg-secondary-100"
                  aria-label={`${photo.shotDate} 사진 열기`}
                >
                  <img
                    src={photo.image}
                    alt={photo.caption || '가족 추억 사진'}
                    className="h-full w-full object-cover transition-transform duration-300 group-active:scale-95"
                  />
                  {photo.mediaType === 'video' && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/15">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white">
                        <i className="ri-play-fill text-xl" />
                      </span>
                    </span>
                  )}
                  <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/65 to-transparent px-2 pb-2 pt-6 text-left text-[11px] font-medium text-white">
                    {photo.shotDate}
                  </span>
                </button>
              ))}
            </section>

            {photos.length === 0 && (
              <div className="rounded-3xl border border-background-200 bg-white p-10 text-center text-sm text-foreground-500">
                현재 볼 수 있는 사진이 없습니다.
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
